import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
  Box,
  StepButton,
  Tooltip,
  useTheme,
  styled,
  IconButton,
  alpha
} from '@mui/material';
import {
  Upload as FileUploadIcon,
  Lightbulb as TopicAnalysisIcon,
  Hub as SemanticIcon,
  Settings as BatchConfigIcon,
  PlayArrow as BatchRunIcon,
  Search as SerpToolIcon,
  TrendingUp as DataForSeoIcon,
  Summarize as WorkflowSummaryIcon,
  Dashboard as ExploreResultsIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon
} from '@mui/icons-material';
import { workflowService } from '../../services/api/workflowService';

interface WorkflowV2StepperProps {
  clientId: string;
  workflowId: string;
  onCollapseChange?: (collapsed: boolean) => void;
  initialCollapsed?: boolean;
}

interface StepStatus {
  completed: boolean;
  inProgress: boolean;
  pending: boolean;
}

interface WorkflowModule {
  type: string;
  name: string;
  status: string;
  module_id?: number;
  module_type?: string;
  module_name?: string;
}

const VerticalStepperContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: theme.spacing(2),
  maxHeight: 'calc(100vh - 40px)',
  overflowY: 'auto',
  width: '280px',
  minWidth: '280px',
  maxWidth: '280px',
  marginRight: theme.spacing(0),
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.1), 0 7px 10px -5px rgba(0, 0, 0, 0.04)',
  '&:hover': {
    boxShadow: '0 6px 22px 0 rgba(0, 0, 0, 0.12), 0 8px 12px -6px rgba(0, 0, 0, 0.06)',
  },
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  zIndex: 10,
}));

const CollapsedStepperContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'sticky',
  top: theme.spacing(2),
  maxHeight: 'calc(100vh - 40px)',
  overflowY: 'auto',
  width: '60px',
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.1), 0 7px 10px -5px rgba(0, 0, 0, 0.04)',
  '&:hover': {
    boxShadow: '0 6px 22px 0 rgba(0, 0, 0, 0.12), 0 8px 12px -6px rgba(0, 0, 0, 0.06)',
  },
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  zIndex: 10,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  transition: theme.transitions.create(['transform', 'background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const GradientTypography = styled(Typography)(() => ({
  fontWeight: 600,
  background: 'linear-gradient(45deg, #332E5C, #00CED1)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}));

const ProcessingStepIcon: React.FC<{ 
  icon: React.ReactNode; 
  isProcessing: boolean; 
  size?: number;
}> = ({ icon }) => {
  return <>{icon}</>;
};

const steps = [
  {
    label: 'File Upload',
    description: 'Upload CSV files for analysis',
    icon: <FileUploadIcon />,
    path: ''
  },
  {
    label: 'Query Analysis',
    description: 'Business setup & query analysis',
    icon: <TopicAnalysisIcon />,
    path: 'keyword'
  },
  {
    label: 'Intent Analysis',
    description: 'Intent categories generation',
    icon: <SemanticIcon />,
    path: 'semantic'
  },
  {
    label: 'Batch Configuration',
    description: 'Configure batch processing',
    icon: <BatchConfigIcon />,
    path: 'batch_config'
  },
  {
    label: 'Batch Processing',
    description: 'Run batch query classification',
    icon: <BatchRunIcon />,
    path: 'batch_run'
  },
  {
    label: 'SERP API Tool',
    description: 'Configure SERP analysis',
    icon: <SerpToolIcon />,
    path: 'serp_tool'
  },
  {
    label: 'DataForSEO Volume Analysis',
    description: 'Get monthly search volume',
    icon: <DataForSeoIcon />,
    path: 'data_for_seo'
  },
  {
    label: 'Workflow Summary',
    description: 'View workflow overview',
    icon: <WorkflowSummaryIcon />,
    path: 'summary'
  },
  {
    label: 'Explore Results',
    description: 'Review and analyze results',
    icon: <ExploreResultsIcon />,
    path: 'explore'
  }
];

const WorkflowV2Stepper: React.FC<WorkflowV2StepperProps> = ({
  clientId,
  workflowId,
  onCollapseChange,
  initialCollapsed = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const savedState = localStorage.getItem('workflowV2StepperCollapsed');
      return savedState !== null ? savedState === 'true' : initialCollapsed;
    } catch {
      return initialCollapsed;
    }
  });
  
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [workflowStatus, setWorkflowStatus] = useState<string>('0');
  const [lastModuleStatus, setLastModuleStatus] = useState<string>('pending');
  
  // Determine active step from current path
  const activeStep = steps.findIndex(step => {
    const currentPath = location.pathname;
    const basePath = `/workflow2/${clientId}/${workflowId}`;
    
    if (step.path === '') {
      return currentPath === basePath;
    }
    return currentPath.includes(`${basePath}/${step.path}`);
  });
  
  // Step statuses state
  const [stepStatuses, setStepStatuses] = useState<Record<number, StepStatus>>({
    0: { completed: false, inProgress: false, pending: true },
    1: { completed: false, inProgress: false, pending: true },
    2: { completed: false, inProgress: false, pending: true },
    3: { completed: false, inProgress: false, pending: true },
    4: { completed: false, inProgress: false, pending: true },
    5: { completed: false, inProgress: false, pending: true },
    6: { completed: false, inProgress: false, pending: true },
    7: { completed: false, inProgress: false, pending: true },
    8: { completed: false, inProgress: false, pending: true },
  });
  
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  
  // Use refs to track the current IDs and prevent duplicate polling
  const currentClientIdRef = useRef<string | null>(null);
  const currentWorkflowIdRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fetch workflow modules and determine step statuses
  useEffect(() => {
    const fetchStepStatuses = async () => {
      // Only fetch if we have valid IDs in props
      if (!clientId || !workflowId) {
        console.log('[Stepper] Skipping fetch - missing IDs');
        return;
      }
      
      try {
        setIsLoadingStatus(true);
        console.log('[Stepper] Fetching status for workflow:', workflowId);
        
        // Get workflow - handles both numeric IDs and external IDs
        // The workflow response already includes all modules, so we don't need a separate call
        const dbWorkflow = await workflowService.getDbWorkflow(workflowId);
        
        // Store workflow status (now a step number or 'completed')
        const currentStatus = (dbWorkflow as any).status || '0';
        setWorkflowStatus(currentStatus);
        console.log('[Stepper] Workflow status:', currentStatus);
        
        // Use modules from the workflow response (already included)
        // Normalize field names: module_type → type, module_name → name
        const modules: WorkflowModule[] = ((dbWorkflow as any).modules || []).map((m: any) => ({
          ...m,
          type: m.type || m.module_type,
          name: m.name || m.module_name
        }));
        console.log('[Stepper] Fetched', modules.length, 'modules from workflow response');
        
        // Store the last module status (most recent module)
        const lastModule = modules.length > 0 ? modules[0] : null;
        const currentLastModuleStatus = lastModule?.status || 'pending';
        setLastModuleStatus(currentLastModuleStatus);
        
        // NEW SIMPLIFIED LOGIC:
        // Workflow status is now a step number (1-7) or 'completed'
        // Calculate step statuses based on workflow status number
        const newStatuses: Record<number, StepStatus> = {};
        const workflowStatusNum = parseInt(currentStatus);
        const isCompleted = currentStatus === 'completed';
        
        // For steps 0-6: Show as completed if workflow status >= their step number + 1
        // (workflow status 1 means step 0 is done, status 2 means steps 0-1 are done, etc.)
        for (let i = 0; i <= 6; i++) {
          if (isCompleted || workflowStatusNum > i) {
            // Previous steps are completed
            newStatuses[i] = { pending: false, inProgress: false, completed: true };
          } else if (workflowStatusNum === i + 1) {
            // Current step - check if last module is completed
            if (currentLastModuleStatus === 'completed') {
              newStatuses[i] = { pending: false, inProgress: false, completed: true };
            } else {
              // Module is in progress or failed
              newStatuses[i] = { pending: false, inProgress: true, completed: false };
            }
          } else {
            // Future steps are pending
            newStatuses[i] = { pending: true, inProgress: false, completed: false };
          }
        }
        
        // Step 7: Workflow Summary - Always accessible, always show as completed
        newStatuses[7] = { pending: false, inProgress: false, completed: true };
        
        // Step 8: Explore Results - Only enabled when workflow status is 'completed'
        if (isCompleted) {
          newStatuses[8] = { pending: false, inProgress: false, completed: true };
        } else {
          newStatuses[8] = { pending: true, inProgress: false, completed: false };
        }
        
        setStepStatuses(newStatuses);
      } catch (error) {
        console.error('Error fetching step statuses:', error);
      } finally {
        setIsLoadingStatus(false);
      }
    };
    
    // Clear any existing polling interval on ID change
    if (pollingIntervalRef.current) {
      console.log('[Stepper] Clearing existing polling interval');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Only set up polling if we have valid IDs
    if (clientId && workflowId) {
      // Update refs
      currentClientIdRef.current = clientId;
      currentWorkflowIdRef.current = workflowId;
      
      // Fetch immediately
      console.log('[Stepper] Starting polling for workflow:', workflowId);
      fetchStepStatuses();
      
      // Set up polling - check status every 10 seconds
      pollingIntervalRef.current = setInterval(() => {
        console.log('[Stepper] Polling workflow status...');
        fetchStepStatuses();
      }, 10000);
    }
    
    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        console.log('[Stepper] Cleaning up polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [clientId, workflowId]);
  
  const handleStepClick = (index: number) => {
    const basePath = `/workflow2/${clientId}/${workflowId}`;
    const targetPath = steps[index].path ? `${basePath}/${steps[index].path}` : basePath;
    navigate(targetPath);
  };
  
  // Helper function to determine if a step should be disabled
  const isStepDisabled = (index: number): boolean => {
    // Step 0: File Upload - always enabled
    if (index === 0) return false;
    
    // Step 7: Workflow Summary - always accessible, never disabled
    if (index === 7) return false;
    
    // Step 8: Explore Results - only enabled when workflow status is 'completed'
    if (index === 8) {
      return workflowStatus !== 'completed';
    }
    
    // For steps 1-6: Enable if previous step is completed OR it's the current step
    // Workflow status = step number currently being worked on
    // Example: status='2' with lastModuleStatus='completed' → enable step 2 (index 1) AND step 3 (index 2)
    const statusNum = parseInt(workflowStatus);
    
    if (!isNaN(statusNum)) {
      // Enable if:
      // 1. Workflow status > this step (previous steps all completed)
      // 2. Workflow status == this step (current step, always accessible)
      // 3. Workflow status == previous step AND last module completed (previous step done, enable next)
      
      if (statusNum > index) {
        // All previous steps completed
        return false;
      } else if (statusNum === index + 1) {
        // This is the current step - always enabled
        return false;
      } else if (statusNum === index && lastModuleStatus === 'completed') {
        // Previous step completed, enable next step
        return false;
      } else {
        // Future steps not yet accessible
        return true;
      }
    }
    
    // Fallback: If workflow status is not a number and not 'completed', check previous step
    const previousStepStatus = stepStatuses[index - 1];
    return !previousStepStatus?.completed;
  };
  
  const toggleCollapsed = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    
    try {
      localStorage.setItem('workflowV2StepperCollapsed', newCollapsedState.toString());
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
    
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  };
  
  if (isCollapsed) {
    return (
      <CollapsedStepperContainer elevation={2}>
        <Tooltip title="Expand Workflow Steps" placement="right">
          <StyledIconButton 
            onClick={toggleCollapsed} 
            sx={{ 
              mb: 2, 
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              }
            }}
          >
            <ExpandIcon />
          </StyledIconButton>
        </Tooltip>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          {steps.map((step, index) => {
            const status = stepStatuses[index as keyof typeof stepStatuses];
            const isDisabled = isStepDisabled(index);
            
            const tooltipTitle = isDisabled
              ? `${step.label}: Complete previous step first`
              : status.inProgress
                ? `${step.label}: Processing...`
                : status.completed
                  ? `${step.label}: Completed`
                : `${step.label}: ${step.description}`;
            
            return (
              <Tooltip 
                key={step.label} 
                title={tooltipTitle}
                placement="right"
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                  }}
                >
                  {/* Active step indicator - left border */}
                  {index === activeStep && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 2,
                        height: '100%',
                        backgroundColor: theme.palette.primary.light,
                        borderRadius: '4px',
                        boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
                      }}
                    />
                  )}
                  
                  <StyledIconButton 
                    color={index === activeStep ? "primary" : "default"}
                    onClick={() => handleStepClick(index)}
                    onMouseEnter={() => setHoveredStep(index)}
                    onMouseLeave={() => setHoveredStep(null)}
                    disabled={isDisabled}
                    sx={{
                      backgroundColor: index === 8 && !isDisabled
                        ? hoveredStep === index
                          ? alpha('#1976d2', 0.18)
                          : alpha('#1976d2', 0.12)
                        : index === activeStep 
                          ? alpha(theme.palette.primary.main, 0.12)
                        : status.inProgress
                          ? alpha(theme.palette.warning.main, 0.08)
                        : hoveredStep === index && !isDisabled
                          ? alpha(theme.palette.primary.main, 0.04)
                          : 'transparent',
                      padding: '10px',
                      '& svg': {
                        color: isDisabled
                          ? theme.palette.action.disabled
                          : index === 8 // Explore Results - show vibrant blue when enabled
                            ? '#1976d2' // Material UI default primary blue
                          : status.inProgress
                            ? theme.palette.warning.main
                          : index === activeStep 
                            ? theme.palette.primary.main 
                            : status.completed 
                              ? theme.palette.success.dark 
                              : theme.palette.text.secondary,
                      },
                      // Blue outline for Explore Results when enabled
                      ...(index === 8 && !isDisabled && {
                        border: `2px solid #1976d2`,
                        backgroundColor: alpha('#1976d2', 0.15),
                      }),
                      // Override for other completed steps
                      ...(status.completed && !status.inProgress && index !== 8 && {
                        border: `2px solid ${theme.palette.success.dark}`,
                        backgroundColor: alpha(theme.palette.success.light, 0.1),
                      }),
                      // Override for in-progress steps
                      ...(status.inProgress && index !== 8 && {
                        border: `2px solid ${theme.palette.warning.main}`,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      }),
                    }}
                  >
                    <ProcessingStepIcon 
                      icon={step.icon} 
                      isProcessing={status.inProgress}
                      size={20}
                    />
                  </StyledIconButton>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </CollapsedStepperContainer>
    );
  }
  
  return (
    <VerticalStepperContainer elevation={2}>
      <Box sx={{ 
        mb: 4, 
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        pb: 2,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center'
      }}>
        <GradientTypography variant="h6" sx={{ fontSize: '1.25rem' }}>
          Workflow Progress
        </GradientTypography>
        <Tooltip title="Collapse Workflow Steps" placement="left">
          <StyledIconButton 
            onClick={toggleCollapsed} 
            size="small"
            sx={{ 
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              }
            }}
          >
            <CollapseIcon />
          </StyledIconButton>
        </Tooltip>
      </Box>
      
      <Stepper 
        activeStep={activeStep === -1 ? 0 : activeStep} 
        orientation="vertical"
        nonLinear
        sx={{ 
          width: '100%',
          '& .MuiStepConnector-line': {
            borderLeftWidth: '2px',
            borderColor: alpha(theme.palette.primary.main, 0.2),
            minHeight: '40px',
          },
          '& .Mui-completed': {
            '& .MuiStepConnector-line': {
              borderColor: theme.palette.success.dark,
            },
            '& .MuiStepIcon-root': {
              color: theme.palette.success.dark,
            }
          },
        }}
      >
        {steps.map((step, index) => {
          const status = stepStatuses[index as keyof typeof stepStatuses];
          const isDisabled = isStepDisabled(index);
          
          return (
            <Step 
              key={step.label}
              completed={status.completed}
            >
              <StepButton 
                onClick={() => handleStepClick(index)}
                optional={
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isDisabled 
                        ? theme.palette.text.disabled
                        : index === 8 // Explore Results - show vibrant blue when enabled
                          ? alpha('#1976d2', 0.9)
                        : status.inProgress
                          ? theme.palette.warning.main
                        : index === activeStep 
                          ? theme.palette.primary.main 
                          : theme.palette.text.secondary
                    }}
                  >
                    {isDisabled ? 'Complete previous step first' : step.description}
                  </Typography>
                }
                disabled={isDisabled}
                sx={{
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: isDisabled 
                      ? 'transparent'
                      : index === 8 && !isDisabled
                        ? alpha('#1976d2', 0.08)
                        : alpha(theme.palette.primary.main, 0.04),
                  },
                  ...(index === activeStep && {
                    backgroundColor: index === 8 
                      ? alpha('#1976d2', 0.12)
                      : alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                      backgroundColor: index === 8 
                        ? alpha('#1976d2', 0.16)
                        : alpha(theme.palette.primary.main, 0.12),
                    },
                    boxShadow: index === 8 
                      ? `0 0 0 1px ${alpha('#1976d2', 0.3)}`
                      : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }),
                  ...(status.completed && !status.inProgress && index !== activeStep && {
                    backgroundColor: index === 8 
                      ? alpha('#1976d2', 0.08)
                      : alpha(theme.palette.success.dark, 0.04),
                  }),
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: index === activeStep ? 600 : 400,
                    color: isDisabled
                      ? theme.palette.text.disabled
                      : index === 8 // Explore Results - show vibrant blue when enabled
                        ? '#1976d2'
                      : status.inProgress
                        ? theme.palette.warning.main
                      : index === activeStep 
                        ? theme.palette.primary.main 
                        : status.completed 
                          ? theme.palette.success.dark 
                          : theme.palette.text.primary
                  }}
                >
                  {step.label}
                </Typography>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </VerticalStepperContainer>
  );
};

export default WorkflowV2Stepper;

